import CryptoJS from 'crypto-js';

const STORAGE_KEY = 'app_secure_data';
const SECRET_KEY = process.env.REACT_APP_STORAGE_SECRET || 'default-key-please-change-in-production';

/**
 * Utilitário para armazenamento seguro de dados sensíveis
 * Criptografa dados antes de armazenar no localStorage
 */
export const secureStorage = {
  /**
   * Armazena um valor criptografado no localStorage
   * @param key Chave do item
   * @param value Valor a ser armazenado
   */
  setItem: (key: string, value: string): void => {
    try {
      const encrypted = CryptoJS.AES.encrypt(value, SECRET_KEY).toString();
      localStorage.setItem(`${STORAGE_KEY}_${key}`, encrypted);
    } catch (error) {
      console.error('Erro ao salvar dado criptografado:', error);
    }
  },
  
  /**
   * Recupera e descriptografa um valor do localStorage
   * @param key Chave do item
   * @returns Valor descriptografado ou null se não existir
   */
  getItem: (key: string): string | null => {
    try {
      const encrypted = localStorage.getItem(`${STORAGE_KEY}_${key}`);
      if (!encrypted) return null;
      
      const decrypted = CryptoJS.AES.decrypt(encrypted, SECRET_KEY);
      const value = decrypted.toString(CryptoJS.enc.Utf8);
      
      // Se a descriptografia falhar, retorna null
      return value || null;
    } catch (error) {
      console.error('Erro ao recuperar dado criptografado:', error);
      return null;
    }
  },
  
  /**
   * Remove um item do localStorage
   * @param key Chave do item
   */
  removeItem: (key: string): void => {
    try {
      localStorage.removeItem(`${STORAGE_KEY}_${key}`);
    } catch (error) {
      console.error('Erro ao remover dado:', error);
    }
  },
  
  /**
   * Limpa todos os dados criptografados do localStorage
   */
  clear: (): void => {
    try {
      Object.keys(localStorage)
        .filter(key => key.startsWith(STORAGE_KEY))
        .forEach(key => localStorage.removeItem(key));
    } catch (error) {
      console.error('Erro ao limpar dados:', error);
    }
  },

  /**
   * Verifica se uma chave existe no storage
   * @param key Chave do item
   * @returns true se a chave existe
   */
  hasItem: (key: string): boolean => {
    return localStorage.getItem(`${STORAGE_KEY}_${key}`) !== null;
  },

  /**
   * Armazena um objeto criptografado
   * @param key Chave do item
   * @param value Objeto a ser armazenado
   */
  setObject: <T>(key: string, value: T): void => {
    try {
      const json = JSON.stringify(value);
      secureStorage.setItem(key, json);
    } catch (error) {
      console.error('Erro ao salvar objeto:', error);
    }
  },

  /**
   * Recupera um objeto criptografado
   * @param key Chave do item
   * @returns Objeto descriptografado ou null
   */
  getObject: <T>(key: string): T | null => {
    try {
      const json = secureStorage.getItem(key);
      if (!json) return null;
      return JSON.parse(json) as T;
    } catch (error) {
      console.error('Erro ao recuperar objeto:', error);
      return null;
    }
  }
};

/**
 * Migra dados do localStorage normal para o storage seguro
 * Use esta função uma vez para migrar dados existentes
 */
export const migrateToSecureStorage = (keys: string[]): void => {
  keys.forEach(key => {
    const value = localStorage.getItem(key);
    if (value) {
      secureStorage.setItem(key, value);
      localStorage.removeItem(key);
    }
  });
};
